import { call, put, takeEvery } from "redux-saga/effects"
import { AppConfiguration } from "config"
import { genericDeleteRequest, genericGetRequest, genericPostRequest } from "helpers/sf_backend_helper"
import { getCurrentBrandFromLocalStorage } from "helpers/sf_utils"
import {
  GET_ACCOUNT_INTEGRATIONS_V2,
  GET_TEMPLATE_INTEGRATIONS_V2,
  GET_CAMPAIGN_ELIGIBLE_INTEGRATIONS_V2,
  GET_CAMPAIGN_ATTACHED_INTEGRATIONS_V2,
  GET_LEAD_INTEGRATION_DETAILS_V2,
  DUPLICATE_INTEGRATION_V2,
  DELETE_INTEGRATION_V2,
} from "./actionTypes"
import {
  getAccountIntegrationsV2Success,
  getAccountIntegrationsV2Error,
  getTemplateIntegrationsV2Success,
  getTemplateIntegrationsV2Error,
  getCampaignEligibleIntegrationsV2Success,
  getCampaignEligibleIntegrationsV2Error,
  getCampaignAttachedIntegrationsV2Success,
  getCampaignAttachedIntegrationsV2Error,
  getLeadIntegrationDetailsV2Success,
  getLeadIntegrationDetailsV2Error,
  duplicateIntegrationV2Success,
  duplicateIntegrationV2Error,
  deleteIntegrationV2Success,
  deleteIntegrationV2Error,
} from "./actions"

const getUser = () => JSON.parse(localStorage.getItem("authUser"))?.user

const getAuthHeaders = (user) => ({
  Authorization: user?.token,
  "account-id": user?.account_id,
  "Content-Type": "application/json",
})

function* onGetAccountIntegrationsV2() {
  try {
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v2/lead_integrations/account_integrations`,
      {},
      getAuthHeaders(getUser())
    )
    yield put(getAccountIntegrationsV2Success(response || []))
  } catch (error) {
    yield put(getAccountIntegrationsV2Error(error))
  }
}

function* onGetTemplateIntegrationsV2() {
  try {
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v2/lead_integrations/template_integrations`,
      {},
      getAuthHeaders(getUser())
    )
    yield put(getTemplateIntegrationsV2Success(response || []))
  } catch (error) {
    yield put(getTemplateIntegrationsV2Error(error))
  }
}

function* onGetCampaignEligibleIntegrationsV2() {
  try {
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v2/lead_integrations/campaign_eligible_integrations`,
      {},
      getAuthHeaders(getUser())
    )
    yield put(getCampaignEligibleIntegrationsV2Success(response || []))
  } catch (error) {
    yield put(getCampaignEligibleIntegrationsV2Error(error))
  }
}

function* onGetCampaignAttachedIntegrationsV2(action) {
  try {
    const campaign_id = action?.payload?.campaign_id
    if (!campaign_id) return

    // brand-id is required here to resolve the campaign within the current brand's scope
    const headers = {
      ...getAuthHeaders(getUser()),
      "brand-id": getCurrentBrandFromLocalStorage()?.id,
    }
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v2/lead_integrations/campaign_attached_integrations`,
      { campaign_id },
      headers
    )
    // Backend returns association rows { lead_integration_id, config }.
    // Consumers read `id` for the referenced integration; rename FK at the edge.
    const rows = (response || []).map(({ lead_integration_id, config }) =>
      ({ id: lead_integration_id, config })
    )
    yield put(getCampaignAttachedIntegrationsV2Success(rows))
  } catch (error) {
    yield put(getCampaignAttachedIntegrationsV2Error(error))
  }
}

function* onGetLeadIntegrationDetailsV2(action) {
  try {
    const integration_id = action?.payload?.integration_id
    if (!integration_id) return

    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/lead_integrations/${integration_id}`,
      {},
      getAuthHeaders(getUser())
    )
    yield put(getLeadIntegrationDetailsV2Success(response))
  } catch (error) {
    yield put(getLeadIntegrationDetailsV2Error({ error, integration_id: action?.payload?.integration_id }))
  }
}

function* onDuplicateIntegrationV2(action) {
  try {
    const integrationId = action?.payload?.id
    const is_template = action?.payload?.is_template
    if (!integrationId) return

    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v2/lead_integrations/${integrationId}/duplicate`,
      {
        body: JSON.stringify({}),
        headers: getAuthHeaders(getUser()),
      }
    )
    yield put(duplicateIntegrationV2Success({ integration: response, is_template }))
  } catch (error) {
    yield put(duplicateIntegrationV2Error({ error, is_template: action?.payload?.is_template }))
  }
}

function* onDeleteIntegrationV2(action) {
  try {
    const integrationId = action?.payload?.id
    const is_template = action?.payload?.is_template
    if (!integrationId) return

    const response = yield call(
      genericDeleteRequest,
      `${AppConfiguration.BACKEND_URL}/api/v2/lead_integrations/${integrationId}`,
      {
        headers: getAuthHeaders(getUser()),
      }
    )
    yield put(deleteIntegrationV2Success({ integration: response, is_template }))
  } catch (error) {
    yield put(deleteIntegrationV2Error({ error, is_template: action?.payload?.is_template }))
  }
}

function* IntegrationV2Saga() {
  yield takeEvery(GET_ACCOUNT_INTEGRATIONS_V2, onGetAccountIntegrationsV2)
  yield takeEvery(GET_TEMPLATE_INTEGRATIONS_V2, onGetTemplateIntegrationsV2)
  yield takeEvery(GET_CAMPAIGN_ELIGIBLE_INTEGRATIONS_V2, onGetCampaignEligibleIntegrationsV2)
  yield takeEvery(GET_CAMPAIGN_ATTACHED_INTEGRATIONS_V2, onGetCampaignAttachedIntegrationsV2)
  yield takeEvery(GET_LEAD_INTEGRATION_DETAILS_V2, onGetLeadIntegrationDetailsV2)
  yield takeEvery(DUPLICATE_INTEGRATION_V2, onDuplicateIntegrationV2)
  yield takeEvery(DELETE_INTEGRATION_V2, onDeleteIntegrationV2)
}

export default IntegrationV2Saga
